import { isDevMode } from "@angular/core";

type MutableT<T> = {
  -readonly [K in keyof T]: T[K];
};

export class Helpers {
  /**
   * Creates a deep copy of {@link object}.
   *
   * @param object any
   * @returns {any} a copy of {@link object}.
   */
  static copy<T>(object: T): MutableT<T> {
    if (object instanceof Date) {
      return new Date(object.getTime()) as T;
    } else if (object instanceof Array) {
      const target: any[] = [];

      for (const obj of object) {
        target.push(Helpers.copy(obj));
      }

      return target as T;
    } else if (object instanceof Object) {
      const target: { [key: string]: any[] } = {};

      for (const prop in object) {
        if (Object.hasOwn(object as any, prop)) {
          target[prop] = Helpers.copy(object[prop]) as any;
        }
      }

      return target as T;
    } else {
      // it's a primitive
      return object;
    }
  }

  static sort(firstValue: string, secondValue: string): number {
    return firstValue.toLowerCase().localeCompare(secondValue.toLowerCase());
  }

  static excludeDuplicates<T>(arr: T[], uniqueProperty: keyof T): T[] {
    const result: T[] = [];
    const map = new Map();

    for (let i = 0; i < arr.length; i++) {
      if (!map.has(arr[i][uniqueProperty])) {
        map.set(arr[i][uniqueProperty], true);
        result.push({ ...arr[i] });
      }
    }

    return result;
  }

  /**
   * @param cacheName Name of the cache to search for (valid names can be found in ngsw-config.json)
   * @param urlSearchTerm The URL to search for in the cache. Partial URLs will be matched.
   * @returns The data found in the cache.
   */
  static async getDataFromServiceWorkerCache<T>(cacheName: string, urlSearchTerm: string): Promise<T> {
    // open the global `caches` object
    const cacheData = await caches.open(`ngsw:/:1:data:${cacheName}:cache`);

    // get all the keys in the cache
    const keys = await cacheData.keys();

    // find the request with a matching url
    const req = keys.find(i => i.url.toLowerCase().includes(urlSearchTerm.toLowerCase()));

    if (!req) {
      return null;
    }

    // get the response from the cache for the desired request
    const response = await caches.match(req);

    // get the StreamReader object to get the data from the response
    const readResult = await response.body.getReader().read();

    // convert the byte array to a string
    const data = String.fromCharCode(...readResult.value);

    // parse the string into json
    return JSON.parse(data);
  }

  /**
   * @returns true if an item was found and deleted, false otherwise
   */
  static async deleteDataFromServiceWorkerCache(cacheName: string, urlSearchTerm: string): Promise<boolean> {
    // open the global `caches` object
    const cacheData = await caches.open(`ngsw:/:1:data:${cacheName}:cache`);

    // get all the keys in the cache
    const keys = await cacheData.keys();

    // find the request with a matching url
    const req = keys.find(i => i.url.toLowerCase().includes(urlSearchTerm.toLowerCase()));

    // delete the entry from cache
    return await cacheData.delete(req);
  }

  /**
   * If the condition is falsy then log a message to the console and raise an exception.
   * @param condition The condition to check
   * @param message The message to log. Also the message for the exception.
   */
  static assert(condition: boolean, message: string): void {
    if (isDevMode() && !condition) {
      console.assert(condition, message);
      throw message;
    }
  }
}

